import { useCallback, useEffect, useState } from 'react';
import { fetchMyEnglishAssessments, type MyEnglishAssessments } from '../api/englishClient';

/**
 * Las evaluaciones de inglés del candidato. La piden el panel, las invitaciones
 * y la barra del Perfil a la vez, así que una misma lectura en vuelo se
 * comparte (y vale unos segundos) en vez de salir tres veces.
 */
let inflight: Promise<MyEnglishAssessments> | null = null;
let inflightAt = 0;
const SHARE_MS = 5_000;
const LIVE_POLL_MS = 15_000;

function load(force = false): Promise<MyEnglishAssessments> {
  const now = Date.now();
  if (!force && inflight && now - inflightAt < SHARE_MS) return inflight;
  inflightAt = now;
  inflight = fetchMyEnglishAssessments();
  inflight.catch(() => {
    inflight = null;
  });
  return inflight;
}

/** Para pruebas. */
export function resetEnglishAssessmentsCache(): void {
  inflight = null;
  inflightAt = 0;
}

export function useEnglishAssessments() {
  const [data, setData] = useState<MyEnglishAssessments | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async (force = true) => {
    try {
      const next = await load(force);
      setData(next);
      setError(false);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload(false);
  }, [reload]);

  // Vuelve de WhatsApp: al recuperar el foco se relee, así el botón de llamar
  // pasa a «calificando» y después al resultado sin recargar la página.
  useEffect(() => {
    const onFocus = () => {
      if (document.visibilityState === 'visible') void reload(false);
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [reload]);

  // Mientras habla o se califica, se mira seguido.
  const live = data?.assessments.some((a) => a.status === 'in_call' || a.status === 'scoring') ?? false;
  useEffect(() => {
    if (!live) return;
    const timer = setInterval(() => void reload(true), LIVE_POLL_MS);
    return () => clearInterval(timer);
  }, [live, reload]);

  return { data, error, loading, reload, setData };
}
